import { isValidElement } from "react";
import { Rnd } from "react-rnd";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import { stdObject } from "../../../data/types/stdObject";
import { emptys, mainConfig } from "../../../data/main";
import { colors } from "../../../data/colors";
import {
  useMainContext,
  useStationeryContext,
} from "../../../context/MainContext";
import { getAlignText } from "../../../utils/getAlignText";
import convertDate from "../../../utils/convertDate";
import { useTranslation } from "react-i18next";
import { toPrice } from "../../../utils/toPrice";
import { newDates, numbersInclude, staDef } from "../../../data/stationery";
import { convertHour } from "../../../utils/convertHour";
import { stationeryObjectCodes } from "../variables/stationeryObjectCodes";
import { updateStationaryItem } from "../../../screens/MainFrame/functions/stationery/updateStationaryItem";
import NOPIC from "../../../assets/nopic.svg";
import BarCodeComp from "../../BarcodeComp";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";

const extraStyle = (e: stdObject, isPrint: boolean, ticketData?: any) => {
  let style: stdObject = {};
  if (!isPrint || e.BORPAR > 0) {
    style.border =
      e.BORPAR > 0 && e.TIPPAR !== 7
        ? `solid ${e.BORPAR}px ${!emptys.includes(e.CBOPAR) ? e.CBOPAR : colors.black}`
        : isPrint
          ? ""
          : `dotted 1px ${colors.black}`;
  } else if (e.TIPPAR >= 9 && e.TIPPAR <= 12 && e.BORPAR === 0) {
    style.border = "none";
  }
  if (e.BOLPAR) {
    style.fontWeight = "bolder";
  }
  if (e.ITAPAR) {
    style.fontStyle = "italic";
  }
  if (e.TIPPAR === 14) {
    style.whiteSpace = e.ANCPAR <= 0 ? "pre" : "pre-line";
  } else if (e.ANCPAR <= 0) {
    style.whiteSpace = "nowrap";
  }
  if (e.TRAPAR > 0) {
    style.opacity = 1 - e.TRAPAR / 100;
  }
  if (e.GRAPAR > 0) {
    style.filter = `grayscale(${e.GRAPAR / 100})`;
  }

  const ticketImg = isPrint && e.PARPAR === "IMGART";

  if (e.TIPPAR === 8 || ticketImg) {
    let backgroundImage = `url(${NOPIC})`;
    if (ticketImg && ticketData[e.PARPAR] !== "") {
      backgroundImage = `url('${mainConfig.url}/images/ART/small/${ticketData.IMGUART !== "" ? ticketData.IMGUART : ticketData.CODART}.${ticketData[e.PARPAR]}')`;
    } else if (e.IMGPAR !== "") {
      backgroundImage = `url('${mainConfig.url}/images/stationery/${e.IMGPAR}')`;
    }
    style.backgroundImage = backgroundImage;
    style.backgroundSize = e.PROPAR ? "contain" : "100% 100%";
    style.backgroundRepeat = "no-repeat";
    style.backgroundPosition = "center";
  }

  if (Number(e.VALPAR) > 0) {
    style.display = "flex";
    style.alignItems = Number(e.VALPAR) === 2 ? "flex-end" : "center";
  }
  return style;
};

const getItemStyle = (
  e: stdObject,
  isPrint: boolean,
  isInput: boolean = false,
  ticketData?: stdObject,
) => {
  let tempStyle: stdObject = {
    position: "absolute",
    width: e.ANCPAR > 0 ? Number(e.ANCPAR) : "auto",
    height: e.ALTPAR > 0 ? Number(e.ALTPAR) : "auto",
    left: Number(e.XPAR),
    top: Number(e.YPAR),
    fontSize: e.SIZPAR,
    textAlign: getAlignText(Number(e.ALIPAR)),
    borderRadius: e.REDPAR,
    zIndex: e.NIVPAR,
    color: e.COLPAR,
    backgroundColor: emptys.includes(e.CFOPAR) ? "transparent" : e.CFOPAR,
    ...extraStyle(e, isPrint, ticketData),
  };
  if (isInput) {
    tempStyle[e.TIPPAR === 9 ? "transform" : "fontSize"] =
      e.TIPPAR === 9 ? `scale(${e.SIZPAR / 12})` : e.SIZPAR;
  }
  return tempStyle;
};

export default function EditableItem({
  e,
  currPage,
  totalPage,
  ticketData,
}: stdObject) {
  const { t } = useTranslation();
  const user: any = useSelector((state: RootState) => state.data.user);
  const { staValues, setStaValues } = useMainContext();
  const {
    type,
    isPrint,
    hasTicket,
    scaleInfo,
    assignItem,
    printData,
    inputsValues,
    setInputsValues,
    processTxt,
  } = useStationeryContext();

  const realData = () => {
    switch (e.TIPPAR) {
      case 1:
      case 2:
      case 3:
        const value =
          hasTicket && e.TIPPAR === 2
            ? ticketData[e.PARPAR]
            : e.PARPAR === "FORMNUMBER"
              ? staValues.formId
              : e.PARPAR === "CURRPAGE"
                ? currPage
                : e.PARPAR === "QTYPAGES"
                  ? totalPage
                  : printData[e.TIPPAR][
                      newDates.includes(e.PARPAR) ? `FEC${type}` : e.PARPAR
                    ];
        return !emptys.includes(value)
          ? numbersInclude.some((prefix) => e.PARPAR.startsWith(prefix))
            ? toPrice(value)
            : e.PARPAR === "HOUR"
              ? convertHour(value.split(" ")[1])
              : e.PARPAR.startsWith("FEC") || e.PARPAR === "DATEONLY"
                ? convertDate({
                    info: value,
                    time: e.PARPAR.startsWith("FEC"),
                    t,
                  })
                : value
          : "";
      case 5:
        const priceRate = printData[e.TIPPAR][e.PARPAR];
        return priceRate ? toPrice(priceRate) : "";
      case 13:
        const price = ticketData.PREART?.[user.PAISUC]?.[e.PARPAR]?.PRELTA;
        return price ? toPrice(price) : "";
      case 14:
        const mainIssueData = printData["14"].find(
          (v: stdObject) => `SDD${v.NUMSDD}` === e.PARPAR,
        );
        if (mainIssueData?.TIPSDD === 5) {
          console.log("mainIssueData", mainIssueData);
        }
        if (mainIssueData) {
          switch (mainIssueData.TIPSDD) {
            case 0:
              const iconSx = {
                fontSize: Number(e.SIZPAR),
                color: !emptys.includes(e.COLPAR) ? e.COLPAR : undefined,
              };
              return Number(mainIssueData.CHKSDD) === 1 ? (
                <CheckBoxIcon sx={iconSx} />
              ) : (
                <CheckBoxOutlineBlankIcon sx={iconSx} />
              );
            case 1:
            case 6:
              return mainIssueData.DIGSDD;
            case 3:
            case 8:
              return mainIssueData.TXTSDD.replaceAll("<br>", "\n");
            case 5:
              return (
                <img
                  src={mainIssueData.DRAWING ?? mainIssueData.ORIGINAL ?? NOPIC}
                  onError={(v: any) => (v.target.src = NOPIC)}
                  alt={mainIssueData.CODSDD}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              );
            default:
              return mainIssueData.CHRSDD ?? "";
          }
        } else {
          return "";
        }
      default:
        return processTxt(e);
    }
  };

  const writeSVG = () => (
    <svg width="100%" height="100%">
      <line
        x1={
          e.BOLPAR && e.ITAPAR
            ? "100%"
            : (!e.BOLPAR && !e.ITAPAR) || e.ITAPAR
              ? "0"
              : "50%"
        }
        y1={
          !e.BOLPAR && !e.ITAPAR ? "50%" : e.BOLPAR || e.ITAPAR ? "0%" : "100%"
        }
        x2={e.BOLPAR && e.ITAPAR ? "0" : e.BOLPAR ? "50%" : "100%"}
        y2={e.BOLPAR || e.ITAPAR ? "100%" : "50%"}
        stroke={!emptys.includes(e.CBOPAR) ? e.CBOPAR : colors.black}
        strokeWidth={e.BORPAR}
      />
    </svg>
  );

  const writeData = () => {
    if (e.PARPAR === "EANART") {
      return (
        <BarCodeComp
          params={{
            code: isPrint ? ticketData[e.PARPAR] : "4006381333931",
            size: e.SIZPAR,
            bgcolor: e.CFOPAR,
            color: e.COLPAR,
          }}
        />
      );
    }
    if (e.TIPPAR === 7) {
      return writeSVG();
    }
    const content = isPrint
      ? realData()
      : staDef.includes(e.PARPAR)
        ? t(`s.${e.PARPAR}`)
        : processTxt(e);
    return isValidElement(content)
      ? content
      : (e.PREPAR ?? "") + content + (e.SUFPAR ?? "");
  };

  const writeInput = () => {
    switch (e.TIPPAR) {
      case 9:
      case 10:
      case 11:
      case 12:
        return (
          <input
            type={
              stationeryObjectCodes.find((v: stdObject) => v.value === e.TIPPAR)
                ?.type
            }
            value={inputsValues[e.NUMPAR] ?? ""}
            disabled={!isPrint}
            style={getItemStyle(e, isPrint, true)}
            onChange={(v: { target: { value: string } }) => {
              const value =
                e.TIPPAR === 11
                  ? v.target.value.replace(/[^0-9.]/g, "")
                  : v.target.value;
              setInputsValues({ ...inputsValues, [e.NUMPAR]: value });
            }}
          />
        );
      case 15:
        return (
          <textarea
            value={inputsValues[e.NUMPAR] ?? ""}
            disabled={!isPrint}
            style={{
              ...getItemStyle(e, isPrint, true),
              resize: "none",
              border: "none",
            }}
            onChange={(v: { target: { value: string } }) => {
              const value =
                e.TIPPAR === 11
                  ? v.target.value.replace(/[^0-9.]/g, "")
                  : v.target.value;
              setInputsValues({ ...inputsValues, [e.NUMPAR]: value });
            }}
          />
        );
      default:
        return (
          <div style={getItemStyle(e, isPrint, false, ticketData)}>
            {e.TIPPAR !== 8 && e.PARPAR !== "IMGART" && writeData()}
          </div>
        );
    }
  };

  return isPrint ? (
    writeInput()
  ) : (
    <Rnd
      size={{
        width: e.ANCPAR > 0 ? Number(e.ANCPAR) : e.TIPPAR === 7 ? 10 : "auto",
        height: e.ALTPAR > 0 ? Number(e.ALTPAR) : e.TIPPAR === 7 ? 10 : "auto",
      }}
      position={{ x: Number(e.XPAR), y: Number(e.YPAR) }}
      scale={scaleInfo}
      onDragStop={(_, d) => {
        assignItem(e.NUMPAR, true);
        updateStationaryItem(
          e.NUMPAR,
          {
            XPAR: d.x,
            YPAR: d.y,
          },
          { staValues, setStaValues },
        );
      }}
      onResizeStop={(_, direction, ref, delta, position) => {
        assignItem(e.NUMPAR, true);
        updateStationaryItem(
          e.NUMPAR,
          {
            ANCPAR: ref.offsetWidth,
            ALTPAR: ref.offsetHeight,
            XPAR: position.x,
            YPAR: position.y,
          },
          { staValues, setStaValues },
        );
      }}
      bounds="parent"
    >
      <div
        style={{
          width: "100%",
          height: "100%",
          fontSize: e.SIZPAR,
          textAlign:
            Number(e.ALIPAR) === 1
              ? "center"
              : Number(e.ALIPAR) === 2
                ? "right"
                : "left",
          borderRadius: e.REDPAR,
          zIndex: e.NIVPAR,
          color: e.COLPAR,
          backgroundColor: e.CFOPAR,
          ...extraStyle(e, isPrint),
        }}
      >
        {e.TIPPAR !== 8 && writeData()}
      </div>
    </Rnd>
  );
}
